// ============================================================================
// Safe Care - Visualisation / consultation détaillée (/assurance/visualisation)
// Module : features/assistance_sociale/pages
//
// Lecture seule : recherche d'une souscription (client, produit, statut) puis
// fiche à onglets : résumé, personnes couvertes, échéancier, indemnisations,
// historique des statuts, règles de la version souscrite.
// Réutilise uniquement les endpoints existants (aucune écriture).
// ============================================================================

import { useMemo, useState, type ReactNode } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { Button, Descriptions, Form, Input, Progress, Select, Space, Spin, Table, Tabs, Tag, Timeline, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import dayjs from 'dayjs';
import ExportMenu, { EXPORT_FORMATS_DOCS } from '../../../Components/ExportMenu';
import ExportFileModal from '../components/ExportFileModal';
import { ExportFile, sectionsFile, singleTableFile } from '../services/exportFile';
import {
  useGetAllSouscriptions,
  useGetAllProduits,
  useGetPersonnesCouvertes,
  useGetEcheancesBySouscription,
  useGetAllDemandesAssistance,
  useGetHistorique,
  useGetEvenementsByVersion,
  useGetCotisation,
  useGetCarence,
  useGetRupture,
  useGetInterruption,
} from '../hooks';
import {
  SouscriptionSafeCare, PersonneCouverte, EcheanceAssistance, DemandeAssistance,
  HistoriqueStatut, EvenementCouvert, RegleCarence,
} from '../types';

const STATUT_COLOR: Record<string, string> = {
  ACTIVE: 'green', SUSPENDUE: 'orange', RESILIEE: 'red', ECHUE: 'default',
  A_PAYER: 'blue', PAYEE: 'green', IMPAYEE: 'red', ANNULEE: 'default',
  EN_COURS: 'orange', VALIDEE: 'green', REJETEE: 'red', EN_ATTENTE: 'orange',
};

// Libellés en clair des statuts techniques (historique lisible par le client/agent)
const STATUT_LABEL: Record<string, string> = {
  ACTIVE: 'Active', SUSPENDUE: 'Suspendue', RESILIEE: 'Résiliée', ECHUE: 'Échue',
  A_PAYER: 'À payer', PAYEE: 'Payée', IMPAYEE: 'Impayée', ANNULEE: 'Annulée',
  EN_COURS: 'En cours', VALIDEE: 'Validée', REJETEE: 'Rejetée', EN_ATTENTE: 'En attente',
  BROUILLON: 'Brouillon', PUBLIE: 'Publiée', DEPRECIE: 'Dépréciée', CLOTUREE: 'Clôturée',
};
const libStatut = (s?: string) => (s ? STATUT_LABEL[s] ?? s : '');

// Libellé lisible d'un événement SMS envoyé (gabarit → texte métier)
const STATUT_ENVOI_LABEL: Record<string, string> = {
  ENFILEE: 'envoyé au client', SANS_TELEPHONE: 'non envoyé (pas de téléphone)',
};

// Explication textuelle affichée en infobulle au survol d'un événement
// de l'historique : reformule le motif technique en phrase métier.
const explicationEvt = (h: HistoriqueStatut): ReactNode => {
  const par = h.NOMUTI ?? h.Coduti;
  if (h.TypeEvt === 'SMS') {
    return (
      <div style={{ maxWidth: 300 }}>
        <b>SMS client — {h.LibelleSms ?? h.CodSms}</b>
        <div>
          {h.StatutEnvoi === 'SANS_TELEPHONE'
            ? "Le SMS n'a pas pu être envoyé : aucun numéro de téléphone n'est enregistré pour ce client."
            : `Un SMS a été envoyé au client au ${h.Telephone ?? 'numéro inconnu'} pour l'informer de cet événement.`}
        </div>
        <div style={{ fontSize: 11, opacity: .8 }}>
          Gabarit {h.CodSms} — texte modifiable dans l'écran des gabarits SMS (FormatSms).
        </div>
      </div>
    );
  }
  const motif = h.Motif ?? '';
  const auto = /automatique/i.test(motif) || (h.Coduti ?? '').toUpperCase() === 'SYSTEME';
  let texte: string;
  if (!h.StatutPrecedent) {
    texte = `Création du contrat : ${motif || 'la souscription devient active après validation de la demande.'}`;
  } else if (h.StatutNouveau === 'SUSPENDUE' && auto) {
    texte = `Suspension automatique : ${motif}. Le contrat ne couvre plus les événements tant que le client ne régularise pas ou que l'agence ne réactive pas.`;
  } else if (h.StatutNouveau === 'SUSPENDUE') {
    texte = `Suspension manuelle validée par l'agence. Motif : « ${motif} ». Le contrat ne couvre plus les événements jusqu'à réactivation.`;
  } else if (h.StatutPrecedent === 'SUSPENDUE' && h.StatutNouveau === 'ACTIVE') {
    texte = `Réactivation : le contrat redevient actif et couvre de nouveau les événements prévus.${motif ? ` Motif : « ${motif} ».` : ''}`;
  } else if (['RESILIEE', 'ECHUE', 'CLOTUREE'].includes(h.StatutNouveau)) {
    texte = `${libStatut(h.StatutNouveau)} : le contrat est définitivement terminé — plus de cotisation ni de couverture.${motif ? ` Motif : « ${motif} ».` : ''}`;
  } else if (h.StatutNouveau === h.StatutPrecedent) {
    texte = `${motif} — information enregistrée sans changement de statut (le contrat reste ${libStatut(h.StatutNouveau).toLowerCase()}).`;
  } else {
    texte = `Passage de « ${libStatut(h.StatutPrecedent)} » à « ${libStatut(h.StatutNouveau)} ».${motif ? ` ${motif}` : ''}`;
  }
  return (
    <div style={{ maxWidth: 300 }}>
      <div>{texte}</div>
      <div style={{ fontSize: 11, opacity: .8 }}>Par {par} — {fmtDateTime(h.DatAction)}</div>
    </div>
  );
};

const StatutTag = ({ s }: { s?: string }) => (s ? <Tag color={STATUT_COLOR[s] ?? 'default'}>{s}</Tag> : null);
const fmtDate = (d?: string) => (d ? dayjs(d).format('DD/MM/YYYY') : '');
const fmtDateTime = (d?: string) => (d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '');
const fmtMontant = (v?: number | null) => (v ?? 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })
  // jsPDF ne rend pas l'espace fine insécable U+202F ni l'insécable U+00A0 -> espace normale
  .replace(/[\u00A0\u202F]/g, ' ');
const table1 = <T,>(res: any): T[] => (res?.StatutCode === 200 ? res.Result?.Table1 ?? [] : []);

const ConsultationSouscriptionPage = () => {
  const [form] = Form.useForm();

  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState<string | undefined>(undefined);
  const [idProduit, setIdProduit] = useState<number | undefined>(undefined);
  const [selected, setSelected] = useState<SouscriptionSafeCare | null>(null);
  const [formatExport, setFormatExport] = useState<'pdf' | 'xlsx' | 'docx'>('pdf');
  const [exportFile, setExportFile] = useState<ExportFile | null>(null);
  const [ficheTab, setFicheTab] = useState('resume');

  const { data: produits } = useGetAllProduits();
  const { data: souscriptions, isLoading: loadingSous, refetch } = useGetAllSouscriptions(statut);

  const filtered = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return (souscriptions ?? []).filter((s: any) => {
      if (idProduit && s.IdProduit !== undefined && s.IdProduit !== idProduit) return false;
      if (idProduit && s.IdProduit === undefined) {
        const p = produits?.find((x: any) => x.IdProduit === idProduit);
        if (p && s.CodeProduit !== p.Code) return false;
      }
      if (!q) return true;
      return [s.Numcli, s.NOMCLI, s.PRECLI, s.NumCptDebit, s.NomProduit, String(s.IdSouscription)]
        .some((v) => (v ?? '').toString().toLowerCase().includes(q));
    });
  }, [souscriptions, recherche, idProduit, produits]);

  const idSous = selected?.IdSouscription;
  const idVersion = selected?.IdVersion;

  const { data: personnesRes, isLoading: lPers } = useGetPersonnesCouvertes(idSous);
  const { data: echeancesRes, isLoading: lEch } = useGetEcheancesBySouscription(idSous);
  const { data: demandesAll, isLoading: lDem } = useGetAllDemandesAssistance();
  const { data: historiqueRes, isLoading: lHist } = useGetHistorique('SOUSCRIPTION', idSous);
  const { data: evenementsRes } = useGetEvenementsByVersion(idVersion);
  const { data: cotisationRes } = useGetCotisation(idVersion);
  const { data: carenceRes } = useGetCarence(idVersion);
  const { data: ruptureRes } = useGetRupture(idVersion);
  const { data: interruptionRes } = useGetInterruption(idVersion);

  const personnes = table1<PersonneCouverte>(personnesRes);
  const echeances = table1<EcheanceAssistance>(echeancesRes);
  const historique = table1<HistoriqueStatut>(historiqueRes);
  const evenements = table1<EvenementCouvert>(evenementsRes);
  const carences = table1<RegleCarence>(carenceRes);
  const cotisation = table1<any>(cotisationRes)[0];
  const rupture = table1<any>(ruptureRes)[0];
  const interruption = table1<any>(interruptionRes)[0];
  const demandes = (demandesAll ?? []).filter((d: DemandeAssistance) => d.IdSouscription === idSous);

  // Export en un seul geste : le format est passé au clic (Dropdown.Button) ;
  // le fichier est généré en mémoire puis la carte animée (Visualiser / Plus tard) s'affiche.
  const exporter = async (fmt: 'pdf' | 'xlsx' | 'docx') => {
    setFormatExport(fmt);
    const run = async (data: any[], titre: string, fichier: string) => {
      if (!data.length) {
        SweetAlert.fire({ title: 'Aucune donnée à exporter', icon: 'warning', timer: 1500 });
        return;
      }
      setExportFile(await singleTableFile(fmt, data, titre, fichier));
    };

    if (!selected) {
      run(filtered.map((s: any) => ({
        'N°': s.IdSouscription, Client: s.Numcli, Nom: `${s.NOMCLI ?? ''} ${s.PRECLI ?? ''}`.trim(), Produit: s.NomProduit,
        Version: s.NumeroVersion, Compte: s.NumCptDebit, Cotisation: s.MontantCotisation,
        Fréquence: s.LibelleFrequence ?? s.CodeFrequence, 'Date effet': fmtDate(s.DateEffet), 'Date fin': fmtDate(s.DateFin), Statut: s.Statut,
      })), 'Safe Care - Liste des souscriptions', 'safecare_souscriptions');
      return;
    }

    // Fiche complète : toutes les informations du client sélectionné,
    // dans le format choisi (un seul document avec toutes les sections).
    const ref = `souscription_${selected.IdSouscription}`;
    const titre = `Fiche souscription n° ${selected.IdSouscription} - ${selected.Numcli} ${selected.NOMCLI ?? ''} ${selected.PRECLI ?? ''}`.trim();
    const fileName = `fiche_${ref}`;

    const sections: { titre: string; data: any[] }[] = [
      {
        titre: 'Résumé',
        data: [{
          'N° souscription': selected.IdSouscription, Client: selected.Numcli, Nom: `${selected.NOMCLI ?? ''} ${selected.PRECLI ?? ''}`.trim(),
          Produit: selected.NomProduit, Version: selected.NumeroVersion, Statut: selected.Statut, Cotisation: selected.MontantCotisation,
          Fréquence: selected.LibelleFrequence ?? selected.CodeFrequence, 'Compte débité': selected.NumCptDebit,
          'Date effet': fmtDate(selected.DateEffet), 'Date fin': fmtDate(selected.DateFin), 'Motif statut': selected.MotifStatut ?? '',
          'Personnes couvertes': personnes.length, 'Échéances payées': `${nbPayees}/${echeances.length}`, 'Impayées': nbImpayees,
          'Total indemnisé': totalIndemnise, 'Nb demandes': demandes.length,
        }],
      },
      {
        titre: 'Personnes couvertes',
        data: personnes.map((p) => ({
          Nom: p.NomBeneficiaire, Prénom: p.PrenomBeneficiaire, 'Date naissance': fmtDate(p.DateNais), 'Lien parenté': p.LienParente, 'Client/Relation': p.Numcli ?? p.NumRel ?? '',
        })),
      },
      {
        titre: 'Échéancier',
        data: echeances.map((e) => ({
          Échéance: fmtDate(e.DateEcheance), Montant: e.MontantCotisation, Payé: e.MontantPaye ?? 0, 'Date paiement': fmtDate(e.DatePaiement), Compte: e.NumCpt ?? '', Statut: e.Statut,
        })),
      },
      {
        titre: 'Indemnisations',
        data: demandes.map((d) => ({
          'N°': d.IdDemandeAssistance, Événement: d.LibelleEvenement, 'Date sinistre': fmtDate(d.DateSinistre), 'Montant demandé': d.MontantDemande ?? 0,
          Justificatifs: d.NbJustificatifs ?? 0, Statut: d.Statut, 'Date décision': fmtDateTime(d.DatVal), 'Motif rejet': d.MotifRejet ?? '',
        })),
      },
      {
        titre: 'Historique',
        data: historique.map((h) => (
          h.TypeEvt === 'SMS'
            ? { Date: fmtDateTime(h.DatAction), Type: 'SMS envoyé', Détail: `${h.LibelleSms ?? h.CodSms} (${h.Telephone ?? ''})`, Par: h.NOMUTI ?? h.Coduti }
            : { Date: fmtDateTime(h.DatAction), Type: 'Statut', Détail: `${h.StatutPrecedent ? libStatut(h.StatutPrecedent) + ' → ' : ''}${libStatut(h.StatutNouveau)}${h.Motif ? ' — ' + h.Motif : ''}`, Par: h.NOMUTI ?? h.Coduti }
        )),
      },
      {
        titre: 'Règles de la version',
        data: evenements.map((ev) => ({
          Code: ev.Code, Libellé: ev.Libelle, Description: ev.Description ?? '',
          'Carence (jours)': carences.find((c) => c.IdEvenement === ev.IdEvenement)?.DureeJours ?? carences.find((c) => !c.IdEvenement)?.DureeJours ?? '',
        })),
      },
    ];

    // Un seul document avec un tableau / une feuille par section
    setExportFile(await sectionsFile(fmt, titre, fileName, sections));
  };

  const nbPayees = echeances.filter((e) => e.Statut === 'PAYEE').length;
  const nbImpayees = echeances.filter((e) => e.Statut === 'IMPAYEE').length;
  const totalPaye = echeances.reduce((a, e) => a + (e.MontantPaye ?? 0), 0);
  const totalDu = echeances.filter((e) => e.Statut !== 'ANNULEE').reduce((a, e) => a + (e.MontantCotisation ?? 0), 0);
  const totalIndemnise = demandes.filter((d) => d.Statut === 'VALIDEE').reduce((a, d) => a + (d.MontantDemande ?? 0), 0);

  // ---------- Indicateurs complémentaires (résumé enrichi) ----------
  // Prochaine échéance, montant réellement impayé, cycles de suspension,
  // dernier SMS client et situation de délai de grâce — dérivés des données
  // déjà chargées, sans nouvel appel API.
  const prochaineEcheance = echeances
    .filter((e) => e.Statut === 'A_PAYER')
    .map((e) => e.DateEcheance).sort()[0];
  const montantImpaye = echeances
    .filter((e) => e.Statut === 'IMPAYEE')
    .reduce((a, e) => a + ((e.MontantCotisation ?? 0) - (e.MontantPaye ?? 0)), 0);
  const nbSuspensions = historique.filter((h) => h.TypeEvt !== 'SMS' && h.StatutNouveau === 'SUSPENDUE').length;
  const nbReactivations = historique.filter((h) => h.TypeEvt !== 'SMS' && h.StatutPrecedent === 'SUSPENDUE' && h.StatutNouveau === 'ACTIVE').length;
  const nbSmsClient = historique.filter((h) => h.TypeEvt === 'SMS' && h.StatutEnvoi === 'ENFILEE').length;
  const dernierSms = historique.find((h) => h.TypeEvt === 'SMS'); // trié récent -> ancien
  // En grâce = contrat encore ACTIVE alors qu'un SMS de délai (SFC14/15) ou
  // un motif « grâce » postérieur à la dernière suspension existe.
  const derniereSuspDate = historique.find((h) => h.TypeEvt !== 'SMS' && h.StatutNouveau === 'SUSPENDUE')?.DatAction;
  const derniereGraceDate = historique.find((h) =>
    (h.TypeEvt === 'SMS' && (h.CodSms === 'SFC14' || h.CodSms === 'SFC15')) || /gr.ce/i.test(h.Motif ?? ''))?.DatAction;
  const enGrace = selected?.Statut === 'ACTIVE' && !!derniereGraceDate &&
    (!derniereSuspDate || dayjs(derniereGraceDate).isAfter(dayjs(derniereSuspDate)));
  const joursGraceRestants = enGrace && interruption?.DelaiRegularisationJours && derniereGraceDate
    ? Math.max(0, interruption.DelaiRegularisationJours - dayjs().diff(dayjs(derniereGraceDate), 'day')) : null;

  const columnsSous: ColumnsType<SouscriptionSafeCare> = [
    { title: 'N°', dataIndex: 'IdSouscription', width: 60 },
    { title: 'Client', dataIndex: 'Numcli', width: 110 },
    { title: 'Nom', render: (_, r) => `${r.NOMCLI ?? ''} ${r.PRECLI ?? ''}`.trim(), ellipsis: true },
    { title: 'Produit', dataIndex: 'NomProduit', ellipsis: true },
    { title: 'Version', dataIndex: 'NumeroVersion', width: 70 },
    { title: 'Cotisation', dataIndex: 'MontantCotisation', width: 100, align: 'right', render: (v) => fmtMontant(v) },
    { title: 'Date effet', dataIndex: 'DateEffet', width: 100, render: fmtDate },
    { title: 'Date fin', dataIndex: 'DateFin', width: 100, render: fmtDate },
    { title: 'Statut', dataIndex: 'Statut', width: 110, render: (s) => <StatutTag s={s} /> },
  ];

  const columnsPers: ColumnsType<PersonneCouverte> = [
    { title: 'Nom', dataIndex: 'NomBeneficiaire' },
    { title: 'Prénom', dataIndex: 'PrenomBeneficiaire' },
    { title: 'Date naissance', dataIndex: 'DateNais', width: 130, render: fmtDate },
    { title: 'Lien parenté', dataIndex: 'LienParente', width: 130 },
    { title: 'Client / Relation', render: (_, r) => r.Numcli ?? r.NumRel ?? '', width: 130 },
  ];

  const columnsEch: ColumnsType<EcheanceAssistance> = [
    { title: 'Échéance', dataIndex: 'DateEcheance', width: 110, render: fmtDate },
    { title: 'Montant', dataIndex: 'MontantCotisation', width: 110, align: 'right', render: (v) => fmtMontant(v) },
    { title: 'Payé', dataIndex: 'MontantPaye', width: 110, align: 'right', render: (v) => fmtMontant(v) },
    { title: 'Date paiement', dataIndex: 'DatePaiement', width: 120, render: fmtDate },
    { title: 'Compte', dataIndex: 'NumCpt', width: 130 },
    {
      title: 'Reste', width: 100, align: 'right',
      render: (_, r) => {
        const reste = (r.MontantCotisation ?? 0) - (r.MontantPaye ?? 0);
        return r.Statut === 'PAYEE' || reste <= 0 ? '' : <span style={{ color: '#dc2626' }}>{fmtMontant(reste)}</span>;
      },
    },
    { title: 'Statut', dataIndex: 'Statut', width: 100, render: (s) => <StatutTag s={s} /> },
  ];

  const columnsDem: ColumnsType<DemandeAssistance> = [
    { title: 'N°', dataIndex: 'IdDemandeAssistance', width: 60 },
    { title: 'Événement', dataIndex: 'LibelleEvenement', ellipsis: true },
    { title: 'Date sinistre', dataIndex: 'DateSinistre', width: 110, render: fmtDate },
    { title: 'Montant demandé', dataIndex: 'MontantDemande', width: 130, align: 'right', render: (v) => fmtMontant(v) },
    { title: 'Justificatifs', dataIndex: 'NbJustificatifs', width: 100, align: 'center' },
    { title: 'Statut', dataIndex: 'Statut', width: 100, render: (s) => <StatutTag s={s} /> },
    { title: 'Date décision', dataIndex: 'DatVal', width: 120, render: fmtDateTime },
    { title: 'Validée par', dataIndex: 'UtiVal', width: 110 },
    { title: 'Motif rejet', dataIndex: 'MotifRejet', ellipsis: true },
  ];

  const columnsEvt: ColumnsType<EvenementCouvert> = [
    { title: 'Code', dataIndex: 'Code', width: 120 },
    { title: 'Libellé', dataIndex: 'Libelle' },
    { title: 'Description', dataIndex: 'Description', ellipsis: true },
    { title: 'Carence (jours)', width: 130, align: 'right', render: (_, r) => carences.find((c) => c.IdEvenement === r.IdEvenement)?.DureeJours ?? carences.find((c) => !c.IdEvenement)?.DureeJours ?? '' },
  ];

  const tabs = selected ? [
    {
      key: 'resume',
      label: 'Résumé',
      children: (
        <>
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="N° souscription">{selected.IdSouscription}</Descriptions.Item>
          <Descriptions.Item label="Client">{selected.Numcli} - {selected.NOMCLI} {selected.PRECLI}</Descriptions.Item>
          <Descriptions.Item label="Statut"><StatutTag s={selected.Statut} /></Descriptions.Item>
          <Descriptions.Item label="Produit">{selected.CodeProduit} - {selected.NomProduit}</Descriptions.Item>
          <Descriptions.Item label="Version">{selected.NumeroVersion}</Descriptions.Item>
          <Descriptions.Item label="Durée contrat">{selected.DureeContratMois ? `${selected.DureeContratMois} mois` : ''}</Descriptions.Item>
          <Descriptions.Item label="Cotisation">{fmtMontant(selected.MontantCotisation)}</Descriptions.Item>
          <Descriptions.Item label="Fréquence">{selected.LibelleFrequence ?? selected.CodeFrequence}</Descriptions.Item>
          <Descriptions.Item label="Compte débité">{selected.NumCptDebit}</Descriptions.Item>
          <Descriptions.Item label="Date effet">{fmtDate(selected.DateEffet)}</Descriptions.Item>
          <Descriptions.Item label="Date fin">{fmtDate(selected.DateFin)}</Descriptions.Item>
          <Descriptions.Item label="Opération cotisation">{selected.lib_CodOpeCotisation ?? selected.CodOpeCotisation}</Descriptions.Item>
          <Descriptions.Item label="Créée par">{selected.UtiSai}</Descriptions.Item>
          <Descriptions.Item label="Créée le">{fmtDateTime(selected.DatCre)}</Descriptions.Item>
          <Descriptions.Item label="Validée par">{selected.UtiVal}</Descriptions.Item>
          <Descriptions.Item label="Validée le">{selected.DatVal ? fmtDateTime(selected.DatVal) : ''}</Descriptions.Item>
          <Descriptions.Item label="Motif statut">{selected.MotifStatut}</Descriptions.Item>
          <Descriptions.Item label="Téléphone">
            {selected.TelephoneClient ?? <Tag color="orange">non renseigné</Tag>}
            {selected.TelephoneClient2 ? ` / ${selected.TelephoneClient2}` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Email">{selected.EmailCli}</Descriptions.Item>
          <Descriptions.Item label="Adresse">{selected.AdresseClient}</Descriptions.Item>
          <Descriptions.Item label="Agence">{selected.CodeAgence}</Descriptions.Item>
          <Descriptions.Item label="Personnes couvertes">{personnes.length}</Descriptions.Item>
          <Descriptions.Item label="Échéances payées">{nbPayees} / {echeances.length} {nbImpayees ? <Tag color="red">{nbImpayees} impayée(s)</Tag> : null}</Descriptions.Item>
          <Descriptions.Item label="Total indemnisé">{fmtMontant(totalIndemnise)} ({demandes.length} demande(s))</Descriptions.Item>
        </Descriptions>
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }} title="Suivi du contrat" style={{ marginTop: 16 }}>
          <Descriptions.Item label="Prochaine échéance">
            {prochaineEcheance ? fmtDate(prochaineEcheance) : <span style={{ color: '#94a3b8' }}>aucune à payer</span>}
          </Descriptions.Item>
          <Descriptions.Item label="Montant impayé">
            {montantImpaye > 0 ? <Tag color="red">{fmtMontant(montantImpaye)}</Tag> : <Tag color="green">0</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Délai de grâce">
            {enGrace
              ? <Tag color="orange">en cours{joursGraceRestants != null ? ` — env. ${joursGraceRestants} j restants` : ''}</Tag>
              : <span style={{ color: '#94a3b8' }}>non</span>}
          </Descriptions.Item>
          <Descriptions.Item label="Suspensions / Réactivations">{nbSuspensions} / {nbReactivations}</Descriptions.Item>
          <Descriptions.Item label="SMS envoyés au client">{nbSmsClient}</Descriptions.Item>
          <Descriptions.Item label="Dernier SMS">
            {dernierSms ? `${dernierSms.CodSms} — ${dernierSms.LibelleSms ?? ''} (${fmtDateTime(dernierSms.DatAction)})` : <span style={{ color: '#94a3b8' }}>aucun</span>}
          </Descriptions.Item>
        </Descriptions>
        </>
      ),
    },
    {
      key: 'personnes',
      label: `Personnes couvertes (${personnes.length})`,
      children: <Spin spinning={lPers}><Table size="small" rowKey="IdPersonneCouverte" columns={columnsPers} dataSource={personnes} pagination={false} /></Spin>,
    },
    {
      key: 'echeances',
      label: `Échéancier (${echeances.length})`,
      children: (
        <Spin spinning={lEch}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Payé {fmtMontant(totalPaye)} sur {fmtMontant(totalDu)} - {nbPayees} payée(s), {nbImpayees} impayée(s)
            </Typography>
            <Progress percent={totalDu ? Math.round((totalPaye / totalDu) * 100) : 0} status={nbImpayees ? 'exception' : 'active'} />
          </Box>
          <Table size="small" rowKey="IdEcheance" columns={columnsEch} dataSource={echeances} pagination={{ pageSize: 12, showSizeChanger: false }} />
        </Spin>
      ),
    },
    {
      key: 'indemnisations',
      label: `Indemnisations (${demandes.length})`,
      children: <Spin spinning={lDem}><Table size="small" rowKey="IdDemandeAssistance" columns={columnsDem} dataSource={demandes} pagination={{ pageSize: 10, showSizeChanger: false }} scroll={{ x: 900 }} /></Spin>,
    },
    {
      key: 'historique',
      label: `Historique (${historique.length})`,
      children: (
        <Spin spinning={lHist}>
          {historique.length === 0 ? <Typography variant="body2" color="textSecondary">Aucun événement enregistré.</Typography> : (
            <Timeline
              mode="left"
              items={historique.map((h) => {
                const isSms = h.TypeEvt === 'SMS';
                return {
                  color: isSms ? 'blue'
                    : STATUT_COLOR[h.StatutNouveau] === 'red' ? 'red'
                    : STATUT_COLOR[h.StatutNouveau] === 'orange' ? 'orange' : 'green',
                  dot: isSms ? <EyeOutlined style={{ fontSize: 12 }} /> : undefined,
                  label: fmtDateTime(h.DatAction),
                  children: (
                    <Tooltip title={explicationEvt(h)} placement="right" color="#0f172a" mouseEnterDelay={0.15}>
                      <div style={{ cursor: 'help', display: 'inline-block' }}>
                        {isSms ? (
                          <>
                            <b>
                              SMS client : {h.LibelleSms ?? h.CodSms}
                            </b>
                            <div style={{ fontSize: 12 }}>
                              Message {h.CodSms} {STATUT_ENVOI_LABEL[h.StatutEnvoi ?? ''] ?? h.StatutEnvoi}
                              {h.Telephone ? ` au ${h.Telephone}` : ''}
                            </div>
                          </>
                        ) : (
                          <>
                            <b>
                              {h.StatutPrecedent ? `${libStatut(h.StatutPrecedent)} → ` : ''}{libStatut(h.StatutNouveau)}
                            </b>
                            <div style={{ fontSize: 12 }}>{h.Motif}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>Par {h.NOMUTI ?? h.Coduti}</div>
                          </>
                        )}
                      </div>
                    </Tooltip>
                  ),
                };
              })}
            />
          )}
        </Spin>
      ),
    },
    {
      key: 'regles',
      label: 'Règles de la version',
      children: (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Descriptions bordered size="small" column={1} title="Cotisation">
              <Descriptions.Item label="Montant">{fmtMontant(cotisation?.Montant)}</Descriptions.Item>
              <Descriptions.Item label="Fréquence">{cotisation?.CodeFrequence}</Descriptions.Item>
              <Descriptions.Item label="Jour de prélèvement">{cotisation?.JourPrelevement}</Descriptions.Item>
            </Descriptions>
          </Grid>
          <Grid item xs={12} md={6}>
            <Descriptions bordered size="small" column={1} title="Interruption / Rupture">
              <Descriptions.Item label="Impayés avant avertissement">{interruption?.NbAvertissement}</Descriptions.Item>
              <Descriptions.Item label="Impayés avant suspension">{interruption?.NbSuspension}</Descriptions.Item>
              <Descriptions.Item label="Impayés avant résiliation">{interruption?.NbResiliation}</Descriptions.Item>
              <Descriptions.Item label="Délai régularisation">{interruption?.DelaiRegularisationJours ? `${interruption.DelaiRegularisationJours} j` : ''}</Descriptions.Item>
              <Descriptions.Item label="Mode rupture">{rupture?.ModeRupture}</Descriptions.Item>
              <Descriptions.Item label="Action après rupture">{rupture?.ActionApresRupture}</Descriptions.Item>
              <Descriptions.Item label="Impayés déclenchant la rupture">{rupture?.NbImpayesRupture}</Descriptions.Item>
            </Descriptions>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Événements couverts</Typography>
            <Table size="small" rowKey="IdEvenement" columns={columnsEvt} dataSource={evenements} pagination={false} />
          </Grid>
        </Grid>
      ),
    },
  ] : [];

  const annuler = () => {
    setRecherche('');
    setStatut(undefined);
    setIdProduit(undefined);
    setSelected(null);
    setFormatExport('pdf');
    setFicheTab('resume');
  };

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>SAFE CARE - VISUALISATION</b>
      </Typography>
      <Spin spinning={loadingSous} size="large" style={{ color: '#116599' }}>
        <Form autoComplete="off" labelAlign="left" form={form} style={{ minWidth: 'auto', maxWidth: 'auto' }}>

          {/* Barre d'action charte (.bG) : toutes les actions se gèrent ici */}
          <div className="bG">
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size="large" className="bt_add" type="ghost" htmlType="button" block onClick={() => refetch()}>
                  Actualiser
                </Button>
                <ExportMenu onExport={(f) => exporter(f as 'pdf' | 'xlsx' | 'docx')} formats={EXPORT_FORMATS_DOCS} />
                <Button size="large" className="bt_add" type="ghost" htmlType="reset" block onClick={annuler}>
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>

          <div className="clientP">
            <div>
              <Tabs
                size="small"
                type="card"
                defaultActiveKey="1"
                items={[{
                  key: '1',
                  label: (
                    <span id="Tab">
                      <EyeOutlined /> Visualisation
                    </span>
                  ),
                  children: (
                <div className="tabItem" style={{ height: '780px', display: 'block' }}>
                  <div style={{ width: '85%', height: '100%', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8 }}>
                  <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
                    <legend>Critères de recherche</legend>
                    <Space wrap>
                      <Input.Search
                        allowClear
                        placeholder="Client, nom, compte, produit, n°..."
                        style={{ width: 280 }}
                        value={recherche}
                        onChange={(e) => setRecherche(e.target.value)}
                      />
                      <Select
                        allowClear
                        placeholder="Tous les statuts"
                        style={{ minWidth: 160 }}
                        value={statut}
                        onChange={(v) => { setStatut(v); setSelected(null); }}
                        options={['ACTIVE', 'SUSPENDUE', 'RESILIEE', 'ECHUE'].map((s) => ({ value: s, label: s }))}
                      />
                      <Select
                        allowClear
                        placeholder="Tous les produits"
                        style={{ minWidth: 220 }}
                        value={idProduit}
                        onChange={(v) => setIdProduit(v)}
                        options={(produits ?? []).map((p: any) => ({ value: p.IdProduit, label: `${p.Code} - ${p.Nom}` }))}
                      />
                    </Space>
                  </Box>

                  <Box fontStyle="inherit" className="border border-left" sx={{ p: 2, mb: 2 }}>
                    <legend>Souscriptions ({filtered.length})</legend>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>Sélectionnez une ligne pour afficher la fiche</Typography>
                    <Spin spinning={loadingSous}>
                      <Table
                        size="small"
                        rowKey="IdSouscription"
                        columns={columnsSous}
                        dataSource={filtered}
                        rowSelection={{ type: 'radio', selectedRowKeys: selected ? [selected.IdSouscription!] : [], onChange: (_, rows) => { setSelected(rows[0] ?? null); setFicheTab('resume'); } }}
                        onRow={(r) => ({ onClick: () => { setSelected(r); setFicheTab('resume'); }, style: { cursor: 'pointer' } })}
                        pagination={{ pageSize: 8, showSizeChanger: false }}
                        scroll={{ x: 900 }}
                      />
                    </Spin>
                  </Box>

                  {selected && (
                    <Box fontStyle="inherit" className="border border-left" sx={{ p: 2 }}>
                      <legend>
                        Fiche souscription n° {selected.IdSouscription} - {selected.NOMCLI} {selected.PRECLI} <StatutTag s={selected.Statut} />
                      </legend>
                      <Tabs size="small" type="card" activeKey={ficheTab} onChange={setFicheTab} items={tabs} />
                    </Box>
                  )}
                  </div>
                </div>
              ),
            }]}
          />
            </div>
          </div>
        </Form>
      </Spin>
      <ExportFileModal file={exportFile} onDone={() => setExportFile(null)} />
    </div>
  );
};

export default ConsultationSouscriptionPage;
